import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AssetUIModel, BuyoutRightsDialogs, TableColumn } from '@thehubz/client/core';
import { AnyType, MediaLists, TerritoryList } from '@thehubz/shared/model';
import { map } from 'rxjs';
import { AssetBuyoutRightsSummaryComponent } from './asset-buyout-rights-summary/asset-buyout-rights-summary.component';
import { AssetBuyoutRightsComponent } from './asset-buyout-rights.component';
import {
  availableBuyoutTitles,
  getGroupedOptions,
  getSelectedCategories,
  getUnmatchedValues,
  singleSelectedCountries,
  updateAvailableBuyoutTitles,
} from './buyout.utils';

@Injectable({ providedIn: 'root' })
export class BuyoutUtilsService {
  constructor(private dialog: MatDialog) {}

  /**
   * Asset buyout right dialog + summary dialog
   * @param uploadedAssets
   * @param viewSummary
   * @param singleAssetUpdate
   * @param editMode
   * @param buyoutRightEditId
   */
  assetBuyoutRightsDialogs(
    uploadedAssets: AssetUIModel[],
    viewSummary = true,
    singleAssetUpdate = false,
    editMode = false,
    index?: number,
    buyoutRightEditId?: string,
  ) {
    const availableTitles = availableBuyoutTitles(uploadedAssets[0]);
    if (editMode && availableTitles.length && uploadedAssets[0].usageRights?.length && index !== undefined) {
      updateAvailableBuyoutTitles(availableTitles, uploadedAssets[0].usageRights, index);
    }
    const dialogRefBuyouts = this.dialog.open<AssetBuyoutRightsComponent, BuyoutRightsDialogs>(AssetBuyoutRightsComponent, {
      maxWidth: '120rem',
      width: '90%',
      height: '90%',
      disableClose: true,
      data: { uploadedAssets, availableTitles, singleAssetUpdate, editMode, index, buyoutRightEditId },
    });

    return dialogRefBuyouts.afterClosed().pipe(
      map((updatedObjs: { responseData: AssetUIModel[]; updatedProperties?: string[] }) => {
        const updatedAssetLists = updatedObjs.responseData;
        // id of the 1st index asset
        const uploadedAssetId = uploadedAssets[0]._id;
        const updatedAsset = updatedAssetLists.find((asset) => asset._id === uploadedAssetId) as AssetUIModel;
        if (viewSummary) {
          this.assetBuyoutRightsSummaryDialogs(updatedAssetLists);
        }
        return { updatedAsset, updatedProperties: updatedObjs.updatedProperties };
      }),
    );
  }

  /**
   * Asset buyout right summary dialog
   * @param updatedAssetLists
   */
  assetBuyoutRightsSummaryDialogs(updatedAssetLists: AssetUIModel[], allTilesUsedFlag?: boolean) {
    const dialogRefBuyouts = this.dialog.open<AssetBuyoutRightsSummaryComponent, { updatedAssetLists: AssetUIModel[]; allTilesUsedFlag?: boolean }>(
      AssetBuyoutRightsSummaryComponent,
      {
        maxWidth: '120rem',
        width: '90%',
        height: '90%',
        disableClose: true,
        data: { updatedAssetLists, allTilesUsedFlag },
      },
    );
    return dialogRefBuyouts.afterClosed();
  }

  mediaAndTerritoryFormatting(values: string[], col: TableColumn) {
    if (!values) return;
    if (typeof values === 'string' && values === 'Multiple values') return;
    const selectedValues: string[] = values.map((v: AnyType) => (typeof v === 'string' ? v : v._id));
    const filterSelectedCategory = getSelectedCategories(selectedValues, col);
    const allPossibleOptions = getGroupedOptions(col);
    const toReturn: { finalSelectedValuesArray: string; finalExcludedValues: string[] }[] = [];

    if (filterSelectedCategory.length > 0) {
      filterSelectedCategory.forEach((category) => {
        const currentCategoryOptions: string[] = allPossibleOptions[category._id];

        const filteredValues = selectedValues.filter(
          (value) => currentCategoryOptions.includes(value) && !filterSelectedCategory.some((category) => category._id === value),
        );

        const excludedValues = getUnmatchedValues(currentCategoryOptions, filteredValues);
        toReturn.push({ finalSelectedValuesArray: category._id, finalExcludedValues: excludedValues });
      });
    }

    return toReturn.length ? toReturn : undefined;
  }

  independentSelectedMediaAndTerritoryFormatting(values: string[], col: TableColumn) {
    if (typeof values === 'string' && values === 'Multiple values') {
      return [values];
    }
    if (!values) return;
    const selectedValues = values.map((v: AnyType) => (typeof v === 'string' ? v : v._id));
    const filterSelectedCategory = getSelectedCategories(selectedValues, col);

    if (col.columnDef === 'territory') {
      return singleSelectedCountries(selectedValues, TerritoryList, filterSelectedCategory);
    } else {
      return singleSelectedCountries(selectedValues, MediaLists, filterSelectedCategory);
    }
  }
}
